import React, { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import { registerApi } from "../../services/registerApi";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { Box, Button, Typography, MenuItem } from "@mui/material";
import { CustomTextField } from "../ui/CustomTextField.tsx";
import { ChevronDown } from "lucide-react";

interface PasswordCriteria {
  LENGTH: string;
  NUMBER: string;
  LOWERCASE: string;
  UPPERCASE: string;
  SPECIAL_CHAR: string;
}

interface TextConfig {
  CREATION_USER_TOAST_USER_CREATING: string;
  CREATION_USER_TOAST_USER_CREATED: string;
  CREATION_USER_TOAST_USER_ERROR: string;
  CREATION_USER_TEXT_USERNAME: string;
  CREATION_USER_TEXT_TEMP_PASSWORD: string;
  CREATION_USER_TEXT_EMAIL: string;
  CREATION_USER_TEXT_ROLE: string;
  CREATION_USER_TEXT_USER: string;
  CREATION_USER_TEXT_ADMIN: string;
  CREATION_USER_TEXT_CREATE: string;
  CREATION_USER_TEXT_CLOSE: string;
  CREATION_USER_TEXT_PASSWORD_CRITERIA: PasswordCriteria;
}

interface CreationUserProps {
  textConfig: TextConfig;
  onClose: () => void;
}

interface PasswordValidationCriteria {
  length: boolean;
  number: boolean;
  lowercase: boolean;
  uppercase: boolean;
  specialChar: boolean;
}

function CreationUser({ textConfig, onClose }: CreationUserProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("usuario");
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordCriteria, setShowPasswordCriteria] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const validatePassword = (password: string): PasswordValidationCriteria => {
    const criteria = {
      length: password.length >= 8,
      number: /\d/.test(password),
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      specialChar: /[!@#$%^&*(),.?":{}|<>_]/.test(password),
    };
    return criteria;
  };

  const handleFocus = () => {
    setShowPasswordCriteria(true);
  };

  const handleBlur = (event: React.FocusEvent) => {
    if (toggleButtonRef.current && toggleButtonRef.current.contains(event.relatedTarget as Node)) {
      return;
    }
    setShowPasswordCriteria(false);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      setIsSubmitting(true);
      toast.dismiss();
      toast.warning(textConfig.CREATION_USER_TOAST_USER_CREATING);
      
      const result = await registerApi(
        username,
        password,
        email,
        role === "administrador" ? "admin" : "user"
      );
  
      if (result.status === 204) {
        toast.dismiss();
        toast.success(textConfig.CREATION_USER_TOAST_USER_CREATED);
      } else {
        toast.dismiss();
        toast.error(textConfig.CREATION_USER_TOAST_USER_ERROR);
      }
    } catch (error) {
      console.error("Error al crear el usuario:", error);
      toast.dismiss();
      toast.error(textConfig.CREATION_USER_TOAST_USER_ERROR);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const criteria = validatePassword(password);
    setIsPasswordValid(Object.values(criteria).every(Boolean));
  }, [password]);

  const passwordCriteria = validatePassword(password);

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography color="white" sx={{ mb: 1, fontWeight: 500 }}>
        {textConfig.CREATION_USER_TEXT_USERNAME} <span style={{ color: '#FF3D3D' }}>*</span>
      </Typography>
      <CustomTextField
        isFullWidth
        placeholder={textConfig.CREATION_USER_TEXT_USERNAME}
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        required
      />

      <Typography color="white" sx={{ mb: 1, fontWeight: 500 }}>
        {textConfig.CREATION_USER_TEXT_TEMP_PASSWORD} <span style={{ color: '#FF3D3D' }}>*</span>
      </Typography>
      <Box sx={{ position: 'relative' }}>
        <CustomTextField
          isFullWidth
          type={showPassword ? "text" : "password"}
          placeholder={textConfig.CREATION_USER_TEXT_TEMP_PASSWORD}
          value={password}
          onChange={handlePasswordChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          required
          inputRef={passwordInputRef}
        />
        <Button
          type="button"
          onClick={togglePasswordVisibility}
          sx={{
            position: 'absolute',
            right: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            minWidth: 'auto',
            p: 1,
            color: '#fff'
          }}
          ref={toggleButtonRef}
        >
          {showPassword ? (
            <EyeSlashIcon style={{ height: 20, width: 20 }} />
          ) : (
            <EyeIcon style={{ height: 20, width: 20 }} />
          )}
        </Button>
      </Box>

      {showPasswordCriteria && (
  <Box 
    sx={{ 
      backgroundColor: '#27272A',
      p: 2,
      borderRadius: '8px',
      marginTop: 1,
      marginBottom: 1
    }}
  >
    {[
      { 
        key: 'length', 
        text: textConfig.CREATION_USER_TEXT_PASSWORD_CRITERIA.LENGTH 
      },
      { 
        key: 'number', 
        text: textConfig.CREATION_USER_TEXT_PASSWORD_CRITERIA.NUMBER 
      },
      { 
        key: 'lowercase', 
        text: textConfig.CREATION_USER_TEXT_PASSWORD_CRITERIA.LOWERCASE 
      },
      { 
        key: 'uppercase', 
        text: textConfig.CREATION_USER_TEXT_PASSWORD_CRITERIA.UPPERCASE 
      },
      { 
        key: 'specialChar', 
        text: textConfig.CREATION_USER_TEXT_PASSWORD_CRITERIA.SPECIAL_CHAR 
      }
    ].map(({ key, text }) => (
      <Box 
        key={key} 
        sx={{ 
          display: 'flex', 
          alignItems: 'flex-start', 
          gap: 1.5, 
          mb: 0.5,
          '&:last-child': {
            mb: 0
          }
        }}
      >
        <input
          type="checkbox"
          checked={passwordCriteria[key as keyof PasswordValidationCriteria]}
          readOnly
          style={{ 
            width: '16px', 
            height: '16px', 
            cursor: 'default',
            accentColor: '#22C55E',
            marginTop: '3px'
          }}
        />
        <Typography 
          variant="body2" 
          sx={{ 
            color: '#fff', 
            fontSize: '14px',
            lineHeight: '20px',
            flex: 1,
            paddingRight: '8px'
          }}
        >
          {text}
        </Typography>
      </Box>
    ))}
  </Box>
)}

      <Typography color="white" sx={{ mb: 1, fontWeight: 500 }}>
        {textConfig.CREATION_USER_TEXT_EMAIL} <span style={{ color: '#FF3D3D' }}>*</span>
      </Typography>
      <CustomTextField
        isFullWidth
        type="email"
        placeholder={textConfig.CREATION_USER_TEXT_EMAIL}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />

      <Typography color="white" sx={{ mb: 1, fontWeight: 500 }}>
        {textConfig.CREATION_USER_TEXT_ROLE} <span style={{ color: '#FF3D3D' }}>*</span>
      </Typography>
      <CustomTextField
        select
        isFullWidth
        value={role}
        onChange={(e) => setRole(e.target.value)}
        required
        sx={{
          '& .MuiInputBase-root': {
            height: '40px',
            backgroundColor: '#2D2D2D',
          },
          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            height: '40px !important',
            padding: '0 14px',
            color: '#fff',
          },
          '& .MuiSelect-icon': {
            color: '#fff',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#22c55e',
            borderWidth: '1px',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#22c55e',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#22c55e',
            borderWidth: '2px',
          },
        }}
        SelectProps={{
          IconComponent: (props) => (
            <ChevronDown
              {...props}
              size={20}
              style={{ 
                color: '#fff',
                right: 8,
              }}
            />
          ),
          MenuProps: {
            PaperProps: {
              sx: {
                backgroundColor: '#3A3A3A',
                borderRadius: '8px',
                marginTop: '4px',
                '& .MuiList-root': {
                  padding: '4px',
                },
                '& .MuiMenuItem-root': {
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: '#4A4A4A',
                  },
                  '&.Mui-selected': {
                    backgroundColor: '#22C55E',
                    '&:hover': {
                      backgroundColor: '#1EA34A',
                    }
                  }
                }
              }
            }
          }
        }}
      >
        <MenuItem value="usuario">{textConfig.CREATION_USER_TEXT_USER}</MenuItem>
        <MenuItem value="administrador">{textConfig.CREATION_USER_TEXT_ADMIN}</MenuItem>
      </CustomTextField>

      <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
        <Button
          type="submit"
          disabled={!isPasswordValid || isSubmitting}
          sx={{
            backgroundColor: "#22C55E",
            color: "#FFFFFF",
            "&:hover": {
              backgroundColor: "#16A34A",
            },
            "&:disabled": {
              backgroundColor: "#1F1F1F",
              color: "#666666",
            },
            textTransform: "none",
            minWidth: "100px",
            borderRadius: "8px",
            padding: "8px 16px",
          }}
        >
          {textConfig.CREATION_USER_TEXT_CREATE}
        </Button>
        <Button
          onClick={onClose}
          sx={{
            backgroundColor: "#FF3D3D",
            color: "#FFFFFF",
            "&:hover": {
              backgroundColor: "#FF5252",
            },
            textTransform: "none",
            minWidth: "100px",
            borderRadius: "8px",
            padding: "8px 16px",
          }}
        >
          {textConfig.CREATION_USER_TEXT_CLOSE}
        </Button>
      </Box>
    </Box>
  );
}

export default CreationUser;